import { DPR, POPUP_DEPTH } from '@constants/common.const';
import Phaser from 'phaser';
import { ShadowNineSlice } from './Shadow9Slice';
import { GameConfigService } from '@services/core/GameConfigService';

export interface PrizePopupConfig {
  scene: Phaser.Scene;
  imagePath?: string;
  text: string;
  x?: number;
  y?: number;
  onHide?: () => void;
}

export class PrizePopup extends Phaser.GameObjects.Container {
  private shadow: ShadowNineSlice;
  private bgRT!: Phaser.GameObjects.RenderTexture;
  private icon: Phaser.GameObjects.Image;
  private label: Phaser.GameObjects.Text;
  private onHideCallback?: () => void;

  constructor(config: PrizePopupConfig) {
    const x = config.x ?? config.scene.scale.width / 2;
    const y = config.y ?? config.scene.scale.height * 0.3;

    super(config.scene, x, y);
    this.onHideCallback = config.onHide;
    this.scene.add.existing(this);
    this.setDepth(POPUP_DEPTH);
    this.setScrollFactor(0);

    // Create shadow (will be sized after content is measured)
    this.shadow = new ShadowNineSlice(
      config.scene,
      0,
      0,
      100 * DPR,
      40 * DPR,
      'rounded',
    );
    this.add(this.shadow);

    // Placeholder RT — sized properly in drawBackground()
    this.bgRT = this.scene.add.renderTexture(0, 0, 1, 1);
    this.bgRT.setOrigin(0, 0);
    this.add(this.bgRT);

    // Create icon
    if (config.imagePath) {
      this.icon = this.scene.add.image(-12 * DPR, 0, config.imagePath);
      this.icon.setDisplaySize(24 * DPR, 24 * DPR);
      this.add(this.icon);
    }

    // Create text
    this.label = this.scene.add.text(12 * DPR, 0, config.text, {
      fontFamily: 'korolev, sans-serif',
      fontSize: `${16 * DPR}px`,
      fontStyle: '500',
      color: new GameConfigService().getColorHex('--color-neutral-100'),
      align: 'left',
      resolution: DPR,
    });
    this.label.setOrigin(0, 0.5);
    this.add(this.label);

    // Draw background based on content size
    this.drawBackground();

    // Set initial alpha to 0 for animation
    this.setAlpha(0);
  }

  private drawBackground(): void {
    const padding = {
      horizontal: 16 * DPR,
      vertical: 8 * DPR,
    };

    const iconWidth = this.icon ? 24 * DPR : 0;
    const gap = 8 * DPR;
    const textWidth = this.label.width;

    const totalWidth = padding.horizontal * 2 + iconWidth + gap + textWidth;
    const height = 40 * DPR;
    const radius = 20 * DPR;

    // Update shadow size and position
    this.shadow.resize(totalWidth, height);
    this.shadow.setPosition(0, 0);

    // Bake rounded rect into RT — no earcut per frame
    this.bgRT.setPosition(-totalWidth / 2, -height / 2);
    this.bgRT.resize(totalWidth, height);
    const gfx = this.scene.add.graphics();
    gfx.fillStyle(new GameConfigService().getColor('--color-base-1')!, 0.9);
    gfx.fillRoundedRect(0, 0, totalWidth, height, radius);
    this.bgRT.draw(gfx, 0, 0);
    gfx.destroy();

    if (this.icon) {
      // Update icon position
      this.icon.setX(-totalWidth / 2 + padding.horizontal + iconWidth / 2);
    }

    // Update text position
    this.label.setX(-totalWidth / 2 + padding.horizontal + iconWidth + gap);

    this.setSize(totalWidth, height);
  }

  /**
   * Show the popup with fade in, wait, and fade out up animation
   */
  public show(duration = 2500, fadeOutDuration = 800): void {
    const fadeInDuration = 500;

    // Bounce in
    this.scene.tweens.add({
      targets: this,
      alpha: 1,
      scale: 1.5,
      duration: fadeInDuration,
      ease: 'Back.easeInOut',
    });

    // Wait and then fade out
    this.scene.time.delayedCall(duration, () => {
      this.fadeOutUp(fadeOutDuration);
    });
  }

  private fadeOutUp(duration: number = 800): void {
    this.scene.tweens.add({
      targets: this,
      alpha: 0,
      scale: 1,
      y: this.y - 120 * DPR,
      duration,
      ease: 'Power2',
      onComplete: () => {
        this.onHideCallback?.();
        this.destroy();
      },
    });
  }
}
